import { sendBeaconEmail, sendBeaconSms, requestQuotaLimits } from '../../../api/modules/managers/outbound';
import type { BeaconCoordinator } from '../../../coordinators/beacon/BeaconCoordinator';

export type OutboundQuotaState = {
  emailCount: number;
  emailLimit: number;
  smsCount: number;
  smsLimit: number;
  reachedEmailLimit: boolean;
  reachedSmsLimit: boolean;
};

export type OutboundSendResult = {
  success: boolean;
  errorMessage?: string;
};

type OutboundListener = (state: OutboundQuotaState) => void;

export class BeaconOutboundManager {
  private beaconCoordinator: BeaconCoordinator;
  private quotaState: OutboundQuotaState = {
    emailCount: 0,
    emailLimit: 20,
    smsCount: 0,
    smsLimit: 10,
    reachedEmailLimit: false,
    reachedSmsLimit: false,
  };
  private listeners: Map<string, OutboundListener> = new Map();
  private listenerCounter = 0;

  constructor(beaconCoordinator: BeaconCoordinator) {
    this.beaconCoordinator = beaconCoordinator;
  }

  public requestQuotas(): void {
    requestQuotaLimits();
  }

  public sendEmail(email: string, message?: string): void {
    const shareCode = this.beaconCoordinator.getShareCode();
    if (!shareCode) {
      console.warn('[BeaconOutboundManager] Cannot send email: no share code');
      return;
    }
    sendBeaconEmail(shareCode, email, message);
    this.quotaState = {
      ...this.quotaState,
      emailCount: this.quotaState.emailCount + 1,
      reachedEmailLimit: this.quotaState.emailCount + 1 >= this.quotaState.emailLimit,
    };
    this.notifyListeners();
  }

  public sendSms(phone: string, message: string): void {
    const shareCode = this.beaconCoordinator.getShareCode();
    if (!shareCode) {
      console.warn('[BeaconOutboundManager] Cannot send SMS: no share code');
      return;
    }
    sendBeaconSms(shareCode, phone, message);
    this.quotaState = {
      ...this.quotaState,
      smsCount: this.quotaState.smsCount + 1,
      reachedSmsLimit: this.quotaState.smsCount + 1 >= this.quotaState.smsLimit,
    };
    this.notifyListeners();
  }

  public getQuotaState(): OutboundQuotaState {
    return this.quotaState;
  }

  public addListener(listener: OutboundListener): string {
    const id = `beacon-outbound-${++this.listenerCounter}`;
    this.listeners.set(id, listener);
    return id;
  }

  public removeListener(id: string): void {
    this.listeners.delete(id);
  }

  public cleanup(): void {
    this.listeners.clear();
  }

  private notifyListeners(): void {
    for (const listener of this.listeners.values()) {
      listener(this.quotaState);
    }
  }
}
